import type { FC, ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { ErrorMessage } from 'formik'
import { Body1, H6, Body2 } from '@dhl-ui/components'

import { FormError } from '@dhl/error'
import { EditSolidIcon } from '@dhl-ui/icons'
import { Button, FormControlLabel, Paper, Radio, Stack, type Sx } from '@dhl/theme'

type DeliveryOptionRadioInputProps = {
  configureButtonText?: string
  editButtonText?: string
  dataDisplayComponent?: React.ReactNode | null
  dataTest?: string
  description?: string
  sx?: Sx
  title: ReactNode
  value: string
  isSelected: boolean
  isCurrentlyActivePreference?: boolean
  hasPreferenceData?: boolean
  onConfigureOptionClick?: () => void
  onChange: (value: string) => void
}

const styles: Record<string, Sx> = {
  paper: (theme) => ({
    backgroundColor: theme.palette.grey[100],
    border: `1px solid ${theme.palette.grey[300]}`,
    color: theme.palette.grey[700],
    padding: theme.spacing(2),
  }),
  activePaper: {
    outlineColor: (theme) => theme.palette.grey[700],
    outlineWidth: '2px',
    outlineStyle: 'solid',
    borderColor: 'transparent',
  },
  activeLabel: {
    color: (theme) => theme.palette.grey[700],
  },
  radio: {
    paddingTop: (theme) => theme.spacing(0),
    paddingBottom: (theme) => theme.spacing(0),
    '&$checked': {
      color: (theme) => theme.palette.grey[700],
    },
  },
  activeRadio: {
    paddingTop: (theme) => theme.spacing(0),
    paddingBottom: (theme) => theme.spacing(0),
  },
  activeRadioLabel: {
    alignItems: 'start',
  },
}

export const DeliveryOptionRadioInput: FC<DeliveryOptionRadioInputProps> = ({
  dataDisplayComponent: DataDisplayComponent,
  configureButtonText,
  editButtonText,
  dataTest,
  description,
  sx,
  title,
  value,
  isSelected,
  isCurrentlyActivePreference,
  hasPreferenceData,
  onConfigureOptionClick,
  onChange,
}) => {
  const { t } = useTranslation()

  return (
    <Paper
      data-test={`${dataTest}-root`}
      elevation={0}
      sx={[styles.paper, isSelected ? styles.activePaper : {}, sx] as Sx}
      onClick={() => onChange(value)}
    >
      <FormControlLabel
        value={value}
        control={<Radio sx={[styles.radio, isCurrentlyActivePreference ? styles.activeRadio : {}] as Sx} />}
        data-test={dataTest}
        label={
          <Stack spacing={0.5}>
            <H6>{title}</H6>
            {isCurrentlyActivePreference && (
              <Body2 variant="success">{t('portal.preferences.radioInput.preferencesSet')}</Body2>
            )}
          </Stack>
        }
        labelPlacement="end"
        sx={isCurrentlyActivePreference ? styles.activeRadioLabel : {}}
      />
      <Stack mt={1} spacing={2}>
        {description && <Body1 sx={{ mt: 1 }}>{description}</Body1>}

        {hasPreferenceData && DataDisplayComponent}

        <ErrorMessage component={FormError} name={value} />

        {isSelected && (
          <>
            {!!onConfigureOptionClick && !hasPreferenceData && (
              <Button
                fullWidth
                data-test="configure-preference-button"
                variant="contained"
                color="black"
                onClick={onConfigureOptionClick}
              >
                {configureButtonText ?? t('portal.preferences.radioInput.configurePreferencesButton')}
              </Button>
            )}

            {!!onConfigureOptionClick && hasPreferenceData && (
              <Button
                fullWidth
                color="primary"
                data-test="edit-preference-button"
                endIcon={<EditSolidIcon />}
                sx={styles.button}
                variant="outlined"
                onClick={onConfigureOptionClick}
              >
                {editButtonText ?? t('portal.preferences.radioInput.editPreferencesButton')}
              </Button>
            )}
          </>
        )}
      </Stack>
    </Paper>
  )
}
